// src/screens/manager/stores/StoreFormModal.tsx
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  useToast,
  FormHelperText,
  FormErrorMessage,
  Switch,
  Box,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { WSStore } from "../../../../services/remote/store/WSStore";
import { Store, StoreStatus } from "../../../../entity/Store";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useTranslation } from "react-i18next";

interface StoreFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (store: Store) => void;
  storeToEdit?: Store | null;
}

const StoreFormModal = ({
  isOpen,
  onClose,
  onSuccess,
  storeToEdit = null,
}: StoreFormModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [isSubdomainValid, setIsSubdomainValid] = useState<boolean | null>(
    null,
  );
  const [subdomainError, setSubdomainError] = useState("");
  const [formData, setFormData] = useState<Store>({
    id: "",
    name: "",
    email: "",
    address: "",
    phoneNumber: "",
    subdomain: "",
    password: "",
    users: [],
    packConfig: {
      packId: undefined,
      expiredAt: undefined,
      isActive: false,
      permissions: [],
    },
  });

  const { t } = useTranslation();
  const debouncedSubdomain = useDebounce(formData.subdomain, 500);

  const toast = useToast();
  const isEditMode = !!storeToEdit;

  const checkSubdomainAvailability = async (subdomain: string) => {
    if (!subdomain) {
      setIsSubdomainValid(null);
      setSubdomainError("");
      return;
    }
    const subdomainRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    if (!subdomainRegex.test(subdomain)) {
      setIsSubdomainValid(false);
      setSubdomainError(t("store.subdomainHelp"));
      return;
    }
    setIsCheckingSubdomain(true);
    try {
      const storeInstance = WSStore.getInstance();
      const existingStore = await storeInstance.getStoreBySubdomain(subdomain);
      setIsSubdomainValid(!existingStore);
      setSubdomainError(existingStore ? t("store.subdomainTaken") : "");
    } catch (error) {
      console.error("Error checking subdomain:", error);
      setIsSubdomainValid(false);
      setSubdomainError(t("error.subdomainCheckError"));
    } finally {
      setIsCheckingSubdomain(false);
    }
  };

  useEffect(() => {
    if (isEditMode && storeToEdit) {
      setFormData(storeToEdit);
    } else {
      setFormData({
        id: "",
        name: "",
        email: "",
        address: "",
        phoneNumber: "",
        subdomain: "",
        password: "",
        status: StoreStatus.ENABLED,
        users: [],
        packConfig: {
          packId: undefined,
          expiredAt: undefined,
          isActive: false,
          permissions: [],
        },
      });
    }
  }, [isEditMode, storeToEdit]);

  useEffect(() => {
    if (!isEditMode) {
      checkSubdomainAvailability(debouncedSubdomain);
    }
  }, [debouncedSubdomain, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEditMode && isSubdomainValid === false) {
      return;
    }
    setIsLoading(true);
    try {
      const storeInstance = WSStore.getInstance();
      let result: Store;
      const updateData = isEditMode
        ? (({ createdAt, updatedAt, ...rest }) => rest)(formData)
        : formData;
      if (isEditMode) {
        result = await storeInstance.updateStore(updateData);
        toast({
          title: t("store.updateSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        result = await storeInstance.createStore(updateData);
        toast({
          title: t("store.createSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      onSuccess(result);
      onClose();
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} store:`,
        error,
      );
      toast({
        title: t(isEditMode ? "store.updateError" : "store.createError"),
        description: error instanceof Error ? error.message : "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditMode ? t("store.editTitle") : t("store.createTitle")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t("store.storeName")}</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t("store.storeNamePlaceholder")}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t("store.email")}</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t("store.emailPlaceholder")}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("store.address")}</FormLabel>
                <Input
                  name="address"
                  value={formData.address || ""}
                  onChange={handleChange}
                  placeholder={t("store.addressPlaceholder")}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("store.phoneNumber")}</FormLabel>
                <Input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber || ""}
                  onChange={handleChange}
                  placeholder={t("store.phonePlaceholder")}
                />
              </FormControl>
              <FormControl isRequired isInvalid={isSubdomainValid === false}>
                <FormLabel>{t("store.subdomain")}</FormLabel>
                <Input
                  name="subdomain"
                  value={formData.subdomain}
                  onChange={handleChange}
                  onBlur={() => checkSubdomainAvailability(formData.subdomain)}
                  placeholder={t("store.subdomainPlaceholder")}
                  disabled={isEditMode}
                  borderColor={
                    isSubdomainValid === true
                      ? "green.400"
                      : isSubdomainValid === false
                        ? "red.400"
                        : "inherit"
                  }
                />
                {isCheckingSubdomain && (
                  <FormHelperText>
                    {t("store.checkingSubdomain")}
                  </FormHelperText>
                )}
                {!isCheckingSubdomain && subdomainError && (
                  <FormErrorMessage>{subdomainError}</FormErrorMessage>
                )}
                {!isCheckingSubdomain &&
                  isSubdomainValid &&
                  formData.subdomain && (
                    <FormHelperText color="green.500">
                      {t("store.subdomainAvailable")}
                    </FormHelperText>
                  )}
              </FormControl>
              {!isEditMode && (
                <FormControl isRequired>
                  <FormLabel>{t("store.password")}</FormLabel>
                  <Input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("store.passwordPlaceholder")}
                  />
                </FormControl>
              )}
              {isEditMode && (
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="status" mb="0">
                    {t("store.storeStatus")}
                  </FormLabel>
                  <Switch
                    id="status"
                    isChecked={formData.status === StoreStatus.ENABLED}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.checked
                          ? StoreStatus.ENABLED
                          : StoreStatus.DISABLED,
                      })
                    }
                    colorScheme="green"
                  />
                  <Box ml={2} fontSize="sm">
                    {formData.status === StoreStatus.ENABLED
                      ? t("store.enabled")
                      : t("store.disabled")}
                  </Box>
                </FormControl>
              )}
              <Button
                type="submit"
                width="full"
                mt={4}
                isLoading={isLoading}
                isDisabled={!isEditMode && isSubdomainValid !== true}
                loadingText={
                  isEditMode ? t("common.updating") : t("common.creating")
                }
              >
                {isEditMode ? t("store.updateButton") : t("store.createButton")}
              </Button>
            </VStack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default StoreFormModal;
